import React, { useMemo, useState, useEffect } from 'react';
import { Form, Row, Col, Accordion, Alert, Table } from 'react-bootstrap';
import {
  FICHA_ODONTO_DEFAULTS,
  getFichaMedicaCatalogosExtrasSync,
  sanitizeContactosEmergencia,
  compactContactosEmergenciaRows,
} from '../../data/consultorioData';
import {
  getProvinciasEcuador,
  getCantonesPorProvincia,
  getCiudadesPorCanton,
  PARENTESCO_EMERGENCIA,
} from '../../data/ecuadorUbicaciones';
import ConsentimientoInformadoSection from './ConsentimientoInformadoSection';
import InformacionGeneralPanel from './InformacionGeneralPanel';
import FichaHistorialClinicaView from './FichaHistorialClinicaView';
import IndicadoresOdontogramaPanel from './IndicadoresOdontogramaPanel';
import { normalizeHigieneOralSimplificada, syncHosLegacyFields } from '../../data/higieneOralSimplificada';
import { normalizeMspIndicadoresPayload } from '../../utils/mspFormStorage';
import { FICHA_ACCORDION_KEYS } from '../../data/fichaAccordionSections';
import { FichaModalNav, FichaAccHeader, FichaFormBlock, FichaModalIntro } from './FichaModalUi';
import { ANTECEDENTES_ODONTO_TABLA_BASE, migrateExamenEstomatognaticoForm } from './fichaOdontologicaLecturaHelpers';
import ExamenEstomatognaticoGrid from './ExamenEstomatognaticoGrid';
import '../../styles/forms.css';
import '../../styles/consultorio.css';

function labelOpcionLista(opts, val) {
  const v = opts.find((o) => String(o.value) === String(val));
  return v ? v.label : val ? String(val) : '';
}

const OPTS_TIPO_IDENTIFICACION = [
  { value: '', label: '—' },
  { value: 'Cédula', label: 'Cédula' },
  { value: 'Pasaporte', label: 'Pasaporte' },
  { value: 'Ruc', label: 'Ruc' }
];

const OPTS_SEXO = [
  { value: '', label: '—' },
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Femenino' },
  { value: 'Otro', label: 'Otro' },
  { value: 'ND', label: 'Prefiero no decirlo' }
];
const OPTS_ESTADO_CIVIL = ['', 'Soltero/a', 'Casado/a', 'Divorciado/a', 'Viudo/a', 'Unión libre', 'Otro'];
const OPTS_TIPO_SEGURO = ['', 'Ninguno', 'IESS', 'Privado', 'Otro'];
const OPTS_HIGIENE = ['', 'Diario (2+ veces)', 'Ocasional', 'Nunca'];
const OPTS_SI_NO = ['', 'Sí', 'No'];
const OPTS_GRUPO_SANGUINEO = ['', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

import PlanDiagnosticoMspTable from './PlanDiagnosticoMspTable';
import PlanTratamientoMspTable from './PlanTratamientoMspTable';
import {
  sanitizeTablaDiagnosticosMSP,
  migrateLegacyDiagnosticosMSPTable,
} from '../../data/planDiagnosticoMsp';
import { sanitizeTablaTratamientosMSP } from '../../data/planTratamientoMsp';

export { sanitizeTablaDiagnosticosMSP } from '../../data/planDiagnosticoMsp';
export { sanitizeTablaTratamientosMSP } from '../../data/planTratamientoMsp';

/** Fila vacía para tabla de medicamentos en ficha médica */
const MED_ROW_BLANK = { nombre: '', dosis: '', frecuencia: '', via: '' };

/** Fila vacía contacto de emergencia */
const CONTACTO_EMERG_BLANK = { nombre: '', telefono: '', relacion: '' };

const ALERGIA_OPCIONES = [
  { id: 'medicamentos', label: 'Medicamentos' },
  { id: 'alimentos', label: 'Alimentos' },
  { id: 'latex', label: 'Látex' },
  { id: 'otros', label: 'Otros' }
];

const OPTS_VIA_ADMIN = [
  '',
  'Oral',
  'Sublingual',
  'Intravenosa (IV)',
  'Intramuscular (IM)',
  'Subcutánea (SC)',
  'Tópica',
  'Transdérmica',
  'Inhalatoria',
  'Intranasal',
  'Oftálmica',
  'Ótica',
  'Rectal',
  'Vaginal',
  'Otro'
];

/** Enfermedades crónicas unificadas (multiselect + Otros texto) */
const ENF_CRONICA_OPCIONES = [
  { id: 'diabetes', label: 'Diabetes' },
  { id: 'hipertension', label: 'Hipertensión arterial' },
  { id: 'hipertiroidismo', label: 'Hipertiroidismo' },
  { id: 'hipotiroidismo', label: 'Hipotiroidismo' },
  { id: 'vih', label: 'VIH' },
  { id: 'covid', label: 'COVID / Secuelas (long COVID)' },
  { id: 'cardiopatia', label: 'Cardiopatía' },
  { id: 'asma', label: 'Asma' },
  { id: 'epoc', label: 'EPOC / Enfermedad pulmonar obstructiva crónica' },
  { id: 'insuf_renal', label: 'Insuficiencia renal crónica' },
  { id: 'hepatopatia', label: 'Enfermedad hepática crónica' },
  { id: 'epilepsia', label: 'Epilepsia' },
  { id: 'artritis', label: 'Artritis / artrosis relevante' },
  { id: 'osteoporosis', label: 'Osteoporosis' },
  { id: 'autoinmune', label: 'Enfermedad autoinmune / reumatológica' },
  { id: 'enf_otros', label: 'Otros' }
];

const HABITO_OPCIONES = [
  { id: 'tabaco', label: 'Tabaco' },
  { id: 'alcohol', label: 'Alcohol' },
  { id: 'drogas', label: 'Drogas' },
  { id: 'bruxismo', label: 'Bruxismo' },
  { id: 'resp_bucal', label: 'Respiración bucal' },
  { id: 'onicofagia', label: 'Onicofagia' },
  { id: 'succion_digital', label: 'Succión digital / hábitos orales infantiles' },
  { id: 'deglucion_atipica', label: 'Deglución atípica' },
  { id: 'apnea_roncar', label: 'Ronquidos / apnea del sueño' },
  { id: 'snacking', label: 'Picoteo / consumo frecuente azúcar' }
];

function mergeAlergiaTiposLista(idDoctor) {
  const ex = getFichaMedicaCatalogosExtrasSync(idDoctor).alergiasTiposExtras.map((e) => ({ id: e.id, label: e.label }));
  return [...ALERGIA_OPCIONES, ...ex];
}

function mergeViasAdministracionLista(idDoctor) {
  const base = OPTS_VIA_ADMIN;
  const ex = getFichaMedicaCatalogosExtrasSync(idDoctor).viaAdministracionExtras.map((e) => (e.label || '').trim()).filter(Boolean);
  const seen = new Set(base.filter(Boolean));
  const out = [...base];
  for (const lbl of ex) {
    if (!seen.has(lbl)) {
      seen.add(lbl);
      out.push(lbl);
    }
  }
  return out;
}

function mergeEnfermedadesCronicasLista(idDoctor) {
  const base = ENF_CRONICA_OPCIONES.filter((o) => o.id !== 'enf_otros');
  const ex = getFichaMedicaCatalogosExtrasSync(idDoctor).enfermedadesCronicasExtras.map((e) => ({ id: e.id, label: e.label }));
  return [...base, ...ex];
}

function mergeHabitosLista(idDoctor) {
  const ex = getFichaMedicaCatalogosExtrasSync(idDoctor).habitosExtras.map((e) => ({ id: e.id, label: e.label }));
  return [...HABITO_OPCIONES, ...ex];
}

/** Historia odontológica — signos vitales (filas base en el consultorio MSP) */
const SIGNOS_ODONTO_BASE_ROWS = [
  { key: 'presionArterial', label: 'Presión arterial' },
  { key: 'frecuenciaCardiacaSignos', label: 'Frecuencia cardíaca' },
  { key: 'temperatura', label: 'Temperatura' },
  { key: 'saturacionOxigeno', label: 'Saturación de oxígeno (SpO₂ / %)' }
];

const ANTECEDENTE_DETALLE_PH_DEFAULT = 'Describa brevemente…';

function mergeOdontoAntecedentesExtras(idDoctor) {
  return getFichaMedicaCatalogosExtrasSync(idDoctor).antecedentesOdontoExtras.map((e) => ({ id: e.id, label: e.label }));
}

function mergeExamenEstomaExtras(idDoctor) {
  return getFichaMedicaCatalogosExtrasSync(idDoctor).examenEstomatognaticoExtras.map((e) => ({ id: e.id, label: e.label }));
}

function mergeSignosVitalesOdontoExtras(idDoctor) {
  return getFichaMedicaCatalogosExtrasSync(idDoctor).signosVitalesOpcionesExtras.map((e) => ({ id: e.id, label: e.label }));
}

function mergeOdontoHigieneLista(idDoctor) {
  const base = OPTS_HIGIENE;
  const extras = getFichaMedicaCatalogosExtrasSync(idDoctor).odontoHigieneExtras.map((e) => (e.label || '').trim()).filter(Boolean);
  const seen = new Set(base.filter(Boolean));
  const out = [...base];
  for (const lbl of extras) {
    if (!seen.has(lbl)) {
      seen.add(lbl);
      out.push(lbl);
    }
  }
  return out;
}

function ensureArr(val) {
  return Array.isArray(val) ? val.filter((x) => x != null && x !== '') : [];
}

function ensureObj(val) {
  return val && typeof val === 'object' && !Array.isArray(val) ? val : {};
}

export function sanitizeMedicamentosFilas(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [{ ...MED_ROW_BLANK }];
  return rows.map((r) => ({
    nombre: (r?.nombre ?? '').toString(),
    dosis: (r?.dosis ?? '').toString(),
    frecuencia: (r?.frecuencia ?? '').toString(),
    via: (r?.via ?? '').toString(),
  }));
}

function trimMedicamentoRow(r) {
  return {
    nombre: (r.nombre || '').trim(),
    dosis: (r.dosis || '').trim(),
    frecuencia: (r.frecuencia || '').trim(),
    via: (r.via || '').trim(),
  };
}

function compactMedicamentosFilas(rows) {
  const filled = sanitizeMedicamentosFilas(rows).filter(rowTieneMedicamento);
  return [...filled, { ...MED_ROW_BLANK }];
}

function rowTieneMedicamento(r) {
  if (!r) return false;
  return !!(r.nombre || r.dosis || r.frecuencia || r.via);
}

function rowTieneContactoEmergencia(r) {
  if (!r) return false;
  return !!(r.nombre || r.telefono || r.relacion);
}

function migrateContactosEmergencia(merged) {
  const fromArray = sanitizeContactosEmergencia(merged.contactosEmergencia);
  if (fromArray.some(rowTieneContactoEmergencia)) {
    merged.contactosEmergencia = fromArray;
  } else if (
    (merged.contactoEmergencia || '').toString().trim() ||
    (merged.telefonoEmergencia || '').toString().trim() ||
    (merged.contactoEmergenciaRelacion || '').toString().trim()
  ) {
    merged.contactosEmergencia = sanitizeContactosEmergencia([
      {
        nombre: merged.contactoEmergencia,
        telefono: merged.telefonoEmergencia,
        relacion: merged.contactoEmergenciaRelacion,
      },
      { ...CONTACTO_EMERG_BLANK },
    ]);
  } else {
    merged.contactosEmergencia = [{ ...CONTACTO_EMERG_BLANK }];
  }
  const first = merged.contactosEmergencia.find(rowTieneContactoEmergencia) || merged.contactosEmergencia[0];
  merged.contactoEmergencia = first?.nombre || '';
  merged.telefonoEmergencia = first?.telefono || '';
  merged.contactoEmergenciaRelacion = first?.relacion || '';
  return merged;
}

function contactosEmergenciaParaVista(form) {
  const rows = sanitizeContactosEmergencia(form.contactosEmergencia).filter(rowTieneContactoEmergencia);
  if (rows.length) return rows;
  const legacy = {
    nombre: (form.contactoEmergencia || '').trim(),
    telefono: (form.telefonoEmergencia || '').trim(),
    relacion: (form.contactoEmergenciaRelacion || '').trim(),
  };
  return rowTieneContactoEmergencia(legacy) ? [legacy] : [];
}

export function migrateHistoriaMedica(form) {
  const merged = form;

  merged.alergiasTipos = ensureArr(merged.alergiasTipos);

  merged.medicamentosActualesFilas = sanitizeMedicamentosFilas(merged.medicamentosActualesFilas);

  migrateContactosEmergencia(merged);

  merged.enfermedadesCronicasSeleccion = ensureArr(merged.enfermedadesCronicasSeleccion);
  merged.enfermedadesCronicasOtro = (merged.enfermedadesCronicasOtro || '').trim();

  merged.habitosSeleccion = ensureArr(merged.habitosSeleccion);

  merged.alergiasEspecificacion = (merged.alergiasEspecificacion ?? '').toString();
  const alEsp = merged.alergiasEspecificacion.trim();
  const alTxt = (merged.alergias || '').toString().trim();
  if (merged.alergiasTipos.length === 0 && !alEsp && alTxt) merged.alergiasEspecificacion = alTxt;

  const filasM = merged.medicamentosActualesFilas;
  const medTxt = (merged.medicamentosActuales || '').toString().trim();
  if (!filasM.some(rowTieneMedicamento) && medTxt) {
    merged.medicamentosActualesFilas = sanitizeMedicamentosFilas([
      { ...MED_ROW_BLANK, nombre: medTxt },
      { ...MED_ROW_BLANK }
    ]);
  }

  const enfTxt = (merged.enfermedadesCronicas || '').toString().trim();
  if (merged.enfermedadesCronicasSeleccion.length === 0 && enfTxt && !merged.enfermedadesCronicasOtro) {
    merged.enfermedadesCronicasOtro = enfTxt;
    if (!merged.enfermedadesCronicasSeleccion.includes('enf_otros')) {
      merged.enfermedadesCronicasSeleccion = [...merged.enfermedadesCronicasSeleccion, 'enf_otros'];
    }
  }

  if (merged.habitosSeleccion.length === 0) {
    const hs = new Set();
    if ((merged.habitosFumar || '').toString().trim()) hs.add('tabaco');
    if ((merged.habitosAlcohol || '').toString().trim() === 'Sí') hs.add('alcohol');
    if ((merged.habitosDrogas || '').toString().trim()) hs.add('drogas');
    merged.habitosSeleccion = [...hs];
  }

  merged.antecedentesOdontoExtrasValores = ensureObj(merged.antecedentesOdontoExtrasValores);
  merged.antecedentesOdontoDetalles = ensureObj(merged.antecedentesOdontoDetalles);
  merged.signosVitalesExtrasValores = ensureObj(merged.signosVitalesExtrasValores);
  migrateExamenEstomatognaticoForm(merged);
  merged.consentimientoInformadoTexto =
    typeof merged.consentimientoInformadoTexto === 'string' ? merged.consentimientoInformadoTexto : '';
  merged.consentimientoInformadoFirmaDataUrl =
    typeof merged.consentimientoInformadoFirmaDataUrl === 'string' ? merged.consentimientoInformadoFirmaDataUrl : '';
  merged.consentimientoInformadoFirmaStorageId =
    typeof merged.consentimientoInformadoFirmaStorageId === 'string' ? merged.consentimientoInformadoFirmaStorageId : '';
  merged.consentimientoInformadoFirmaModo =
    typeof merged.consentimientoInformadoFirmaModo === 'string' ? merged.consentimientoInformadoFirmaModo : '';
  merged.consentimientoInformadoFirmaBytes =
    Number(merged.consentimientoInformadoFirmaBytes) > 0 ? Number(merged.consentimientoInformadoFirmaBytes) : 0;
  merged.planDiagTerapeuticoEducativoMSP = (merged.planDiagTerapeuticoEducativoMSP ?? '').toString();
  merged.tratamientoMSPTextoResumen = (merged.tratamientoMSPTextoResumen ?? '').toString();
  merged.profesionalNombreApellidos = (merged.profesionalNombreApellidos ?? '').toString();
  merged.profesionalRegistroMSP = (merged.profesionalRegistroMSP ?? '').toString();
  merged.profesionalFirmaElectronicaNotas = (merged.profesionalFirmaElectronicaNotas ?? '').toString();

  merged.tablaDiagnosticosMSP = sanitizeTablaDiagnosticosMSP(migrateLegacyDiagnosticosMSPTable(merged));
  merged.tablaTratamientosMSP = sanitizeTablaTratamientosMSP(merged.tablaTratamientosMSP);

  return normalizeMspIndicadoresPayload(merged);
}

/** Textos planos derivados para campos históricos (`alergias`, `medicamentosActuales`, etc.) y compatibilidad MSP */
export function buildHistoriaMedicaForStorage(form, idDoctor) {
  const alOps = mergeAlergiaTiposLista(idDoctor);
  const alTip = ensureArr(form.alergiasTipos);
  const catPart = alTip.map((tid) => alOps.find((o) => o.id === tid)?.label).filter(Boolean).join(', ');
  const esp = (form.alergiasEspecificacion || '').trim();
  let alergias = '';
  if (catPart || esp) {
    alergias = [catPart && `Tipos: ${catPart}`, esp].filter(Boolean).join('. ').trim();
  } else {
    alergias = (form.alergias || '').trim();
  }

  const filas = sanitizeMedicamentosFilas(form.medicamentosActualesFilas)
    .map(trimMedicamentoRow)
    .filter(rowTieneMedicamento);
  let medicamentosActuales = '';
  if (filas.length) {
    medicamentosActuales = filas
      .map((r) => {
        const bits = [
          r.nombre && `Nombre: ${r.nombre}`,
          r.dosis && `Dosis: ${r.dosis}`,
          r.frecuencia && `Frec.: ${r.frecuencia}`,
          r.via && `Vía: ${r.via}`
        ].filter(Boolean);
        return bits.join(' · ');
      })
      .join('\n');
  } else {
    medicamentosActuales = (form.medicamentosActuales || '').trim();
  }

  const enfOpsMain = mergeEnfermedadesCronicasLista(idDoctor);
  const enfSel = ensureArr(form.enfermedadesCronicasSeleccion);
  const enfOtroTxt = (form.enfermedadesCronicasOtro || '').trim();
  const partesEnf = enfSel
    .filter((eid) => eid !== 'enf_otros')
    .map((eid) => enfOpsMain.find((o) => o.id === eid)?.label)
    .filter(Boolean);
  if (enfOtroTxt) partesEnf.push(`Otros: ${enfOtroTxt}`);
  let enfermedadesCronicas = partesEnf.length ? partesEnf.join(' · ') : (form.enfermedadesCronicas || '').trim();

  const hSel = ensureArr(form.habitosSeleccion);
  const habOps = mergeHabitosLista(idDoctor);
  const primary = new Set(['tabaco', 'alcohol', 'drogas']);
  const extra = habOps.filter((o) => hSel.includes(o.id) && !primary.has(o.id)).map((o) => o.label).join(', ');
  const drogasTxt = hSel.includes('drogas') ? 'Consumo declarado de drogas' : '';

  return {
    alergias,
    medicamentosActuales,
    enfermedadesCronicas,
    habitosFumar: hSel.includes('tabaco') ? 'Sí' : '',
    habitosAlcohol: hSel.includes('alcohol') ? 'Sí' : '',
    habitosDrogas: [drogasTxt, extra].filter(Boolean).join('; ')
  };
}

function resumenAlergiasLectura(form, idDoctor) {
  const alOps = mergeAlergiaTiposLista(idDoctor);
  const alTip = ensureArr(form.alergiasTipos);
  const catPart = alTip.map((tid) => alOps.find((o) => o.id === tid)?.label).filter(Boolean).join(', ');
  const esp = (form.alergiasEspecificacion || '').trim();
  return [catPart && `Tipos: ${catPart}`, esp].filter(Boolean).join('. ').trim() || (form.alergias || '').trim();
}

function resumenEnfermedadesLectura(form, idDoctor) {
  const enfOpsMain = mergeEnfermedadesCronicasLista(idDoctor);
  const enfSel = ensureArr(form.enfermedadesCronicasSeleccion);
  const enfOtroTxt = (form.enfermedadesCronicasOtro || '').trim();
  const partes = enfSel
    .filter((eid) => eid !== 'enf_otros')
    .map((eid) => enfOpsMain.find((o) => o.id === eid)?.label)
    .filter(Boolean);
  if (enfOtroTxt) partes.push(`Otros: ${enfOtroTxt}`);
  return partes.length ? partes.join(' · ') : (form.enfermedadesCronicas || '').trim();
}

function resumenHabitosLectura(form, idDoctor) {
  const hSel = ensureArr(form.habitosSeleccion);
  if (!hSel.length) {
    const leg = [
      (form.habitosFumar || '').trim() && `Tabaco: ${form.habitosFumar}`,
      (form.habitosAlcohol || '').trim() && `Alcohol: ${form.habitosAlcohol}`,
      (form.habitosDrogas || '').trim() && `Otros: ${form.habitosDrogas}`
    ].filter(Boolean);
    return leg.join(' · ');
  }
  const habOps = mergeHabitosLista(idDoctor);
  return habOps.filter((o) => hSel.includes(o.id)).map((o) => o.label).join(', ');
}

/** Tabla sólo lectura: filas con valor no vacío. */
function LecturaTablaClaveValor({ titulo, pairs }) {
  const rows = (pairs || []).filter((p) => p.value != null && String(p.value).trim() !== '');
  if (rows.length === 0) return null;
  return (
    <Col xs={12}>
      <div className="ficha-odonto-field-view">
        <span className="ficha-odonto-field-label">{titulo}</span>
        <div className="table-responsive mt-1">
          <table className="table table-sm table-bordered mb-0 small">
            <tbody>
              {rows.map((p) => (
                <tr key={p.k}>
                  <td className="text-muted" style={{ width: '42%' }}>{p.label}</td>
                  <td>{String(p.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Col>
  );
}

/** Texto único para `direccion` (compat. con datos antiguos y exportaciones MSP). */
export function buildDireccionResumen(form) {
  const parts = [
    form.direccionProvincia,
    form.direccionCanton,
    form.direccionParroquia,
    form.direccionCiudad,
    form.direccionExacta
  ]
    .map((x) => (typeof x === 'string' ? x.trim() : x))
    .filter(Boolean);
  if (parts.length) return parts.join(', ');
  return (form.direccion || '').trim();
}

function sexoLabel(sexo) {
  const o = OPTS_SEXO.find((x) => x.value === sexo);
  return o?.label ?? sexo ?? '';
}

/** Rellena el objeto form con defaults y luego con los datos del paciente (para no perder campos guardados) */
export function getInitialFichaForm(paciente) {
  const base = { ...FICHA_ODONTO_DEFAULTS };
  if (!paciente) return migrateHistoriaMedica({ ...base });
  const merged = { ...base };
  Object.keys(paciente).forEach((k) => {
    if (paciente[k] !== undefined && paciente[k] !== null) {
      if (k === 'fechaNacimiento' && paciente[k]) merged[k] = paciente[k].slice(0, 10);
      else if (k === 'consentimientoInformado') merged[k] = !!paciente[k];
      else merged[k] = paciente[k];
    }
  });
  if (!(merged.tipoIdentificacion || '').trim() && (merged.cedula || '').toString().trim()) {
    merged.tipoIdentificacion = 'Cédula';
  }
  if (!(merged.direccionExacta || '').trim() && (merged.direccion || '').trim()) {
    merged.direccionExacta = merged.direccion;
  }
  merged.higieneOralSimplificada = normalizeHigieneOralSimplificada(merged);
  const legacyHos = syncHosLegacyFields(merged.higieneOralSimplificada);
  if (!merged.placa && legacyHos.placa) merged.placa = legacyHos.placa;
  if (!merged.calculo && legacyHos.calculo) merged.calculo = legacyHos.calculo;
  if (!merged.gingivitis && legacyHos.gingivitis) merged.gingivitis = legacyHos.gingivitis;
  return migrateHistoriaMedica(merged);
}

const FichaOdontologicaForm = ({
  form,
  setForm,
  readOnly = false,
  loadingGeneral = false,
  idDoctor,
  tenantId = null,
  storageLimitMb = null,
  accordionMountKey = 0,
  defaultAccordionKey = 'personal',
  inModal = false,
  onEditSection = null,
  onGoToMspTratamiento = null,
  usuarioProfesionalEmail = '',
}) => {
  const [activeAccordionKey, setActiveAccordionKey] = useState(defaultAccordionKey || FICHA_ACCORDION_KEYS.personal);

  useEffect(() => {
    setActiveAccordionKey(defaultAccordionKey || FICHA_ACCORDION_KEYS.personal);
  }, [defaultAccordionKey, accordionMountKey]);
  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const alergiasTiposLista = useMemo(() => mergeAlergiaTiposLista(idDoctor), [idDoctor]);
  const viasAdministracionLista = useMemo(() => mergeViasAdministracionLista(idDoctor), [idDoctor]);
  const enfCronicasLista = useMemo(() => mergeEnfermedadesCronicasLista(idDoctor), [idDoctor]);
  const habitosLista = useMemo(() => mergeHabitosLista(idDoctor), [idDoctor]);
  const odontoAntecedenteExtras = useMemo(() => mergeOdontoAntecedentesExtras(idDoctor), [idDoctor]);
  const odontoExamenExtras = useMemo(() => mergeExamenEstomaExtras(idDoctor), [idDoctor]);
  const odontoSignosExtras = useMemo(() => mergeSignosVitalesOdontoExtras(idDoctor), [idDoctor]);
  const odontoHigieneOpciones = useMemo(() => mergeOdontoHigieneLista(idDoctor), [idDoctor]);

  const patchObjCampoPaciente = (mapKey, rowId, valor) => {
    setForm((f) => ({
      ...f,
      [mapKey]: { ...ensureObj(f[mapKey]), [rowId]: valor }
    }));
  };

  const patchAntecedenteDetalle = (rowKey, valor) => {
    setForm((f) => ({
      ...f,
      antecedentesOdontoDetalles: { ...ensureObj(f.antecedentesOdontoDetalles), [rowKey]: valor }
    }));
  };

  const handleAntecedenteRefiere = (key, valor) => {
    setForm((f) => {
      const next = { ...f, [key]: valor };
      if (valor !== 'Sí') {
        next.antecedentesOdontoDetalles = { ...ensureObj(f.antecedentesOdontoDetalles), [key]: '' };
      }
      return next;
    });
  };

  const handleAntecedenteExtraRefiere = (exId, valor) => {
    setForm((f) => {
      const next = {
        ...f,
        antecedentesOdontoExtrasValores: { ...ensureObj(f.antecedentesOdontoExtrasValores), [exId]: valor }
      };
      if (valor !== 'Sí') {
        next.antecedentesOdontoDetalles = { ...ensureObj(f.antecedentesOdontoDetalles), [exId]: '' };
      }
      return next;
    });
  };

  const placeholderNumIdent =
    !form.tipoIdentificacion
      ? 'Número según el tipo elegido'
      : form.tipoIdentificacion === 'Pasaporte'
        ? 'Número de pasaporte'
        : form.tipoIdentificacion === 'Ruc'
          ? 'Número de RUC'
          : 'Ej. cédula 10 dígitos (EC)';

  const toggleEnLista = (campoLista, id, checked) => {
    setForm((f) => {
      const cur = ensureArr(f[campoLista]);
      const next = checked ? [...new Set([...cur, id])] : cur.filter((x) => x !== id);
      return { ...f, [campoLista]: next };
    });
  };

  const filasMedUi = compactMedicamentosFilas(form.medicamentosActualesFilas);

  const updateMedFila = (i, field, val) => {
    setForm((f) => ({
      ...f,
      medicamentosActualesFilas: compactMedicamentosFilas(f.medicamentosActualesFilas).map((row, idx) =>
        idx === i ? { ...row, [field]: val } : row
      ),
    }));
  };

  const addMedFila = () =>
    setForm((f) => {
      const rows = compactMedicamentosFilas(f.medicamentosActualesFilas);
      const last = rows[rows.length - 1];
      const next = rowTieneMedicamento(last) ? [...rows, { ...MED_ROW_BLANK }] : rows;
      return { ...f, medicamentosActualesFilas: next };
    });

  const removeMedFila = (i) => {
    setForm((f) => {
      const rows = compactMedicamentosFilas(f.medicamentosActualesFilas);
      const next = rows.filter((_, idx) => idx !== i);
      return { ...f, medicamentosActualesFilas: next.length ? next : [{ ...MED_ROW_BLANK }] };
    });
  };

  const provinciasEcuador = useMemo(() => getProvinciasEcuador(), []);
  const cantonesEcuador = useMemo(
    () => getCantonesPorProvincia(form.direccionProvincia),
    [form.direccionProvincia]
  );
  const localidadesCanton = useMemo(
    () => getCiudadesPorCanton(form.direccionProvincia, form.direccionCanton),
    [form.direccionProvincia, form.direccionCanton]
  );

  const opcionesConValorGuardado = (lista, valor) => {
    const cur = (valor || '').trim();
    if (!cur || lista.includes(cur)) return lista;
    return [...lista, cur];
  };

  const onProvinciaChange = (e) => {
    const v = e.target.value;
    setForm((f) => ({
      ...f,
      direccionProvincia: v,
      direccionCanton: '',
      direccionParroquia: '',
      direccionCiudad: '',
    }));
  };

  const onCantonChange = (e) => {
    const v = e.target.value;
    setForm((f) => ({
      ...f,
      direccionCanton: v,
      direccionParroquia: '',
      direccionCiudad: '',
    }));
  };

  const filasContactoUi = compactContactosEmergenciaRows(form.contactosEmergencia);

  const syncContactoLegacy = (rows) => {
    const compact = compactContactosEmergenciaRows(rows);
    const first = compact.find(rowTieneContactoEmergencia) || { ...CONTACTO_EMERG_BLANK };
    return {
      contactosEmergencia: compact,
      contactoEmergencia: first.nombre || '',
      telefonoEmergencia: first.telefono || '',
      contactoEmergenciaRelacion: first.relacion || '',
    };
  };

  const updateContactoEmergencia = (i, field, val) => {
    setForm((f) => {
      const rows = compactContactosEmergenciaRows(f.contactosEmergencia).map((row, idx) =>
        idx === i ? { ...row, [field]: val } : row
      );
      return { ...f, ...syncContactoLegacy(rows) };
    });
  };

  const addContactoEmergencia = () =>
    setForm((f) => {
      const rows = compactContactosEmergenciaRows(f.contactosEmergencia);
      const last = rows[rows.length - 1];
      const next = rowTieneContactoEmergencia(last)
        ? [...rows, { ...CONTACTO_EMERG_BLANK }]
        : rows;
      return { ...f, ...syncContactoLegacy(next) };
    });

  const removeContactoEmergencia = (i) => {
    setForm((f) => {
      const rows = compactContactosEmergenciaRows(f.contactosEmergencia);
      const next = rows.filter((_, idx) => idx !== i);
      return { ...f, ...syncContactoLegacy(next.length ? next : [{ ...CONTACTO_EMERG_BLANK }]) };
    });
  };

  if (readOnly) {
    return (
      <div className="ficha-odonto-view">
        <div className="fo-general-section">
          <div className="fo-general-section-head fo-general-section-head--row">
            <div>
              <h3 className="fo-general-section-title">
                <i className="bi bi-person-lines-fill" />
                Información general
              </h3>
              <span className="fo-general-section-desc">Datos de identificación, contacto y ubicación del paciente</span>
            </div>
            {onEditSection ? (
              <button
                type="button"
                className="cp-btn cp-btn--ghost cp-btn--sm"
                onClick={() => onEditSection('personal')}
              >
                <i className="bi bi-pencil" />
                Editar
              </button>
            ) : null}
          </div>
          <InformacionGeneralPanel form={form} loading={loadingGeneral} />
        </div>

        <FichaHistorialClinicaView
          form={form}
          idDoctor={idDoctor}
          tenantId={tenantId}
          onEditSection={onEditSection}
          onGoToMspTratamiento={onGoToMspTratamiento}
        />
      </div>
    );
  }

  const accordionProps = inModal
    ? {
        activeKey: activeAccordionKey,
        onSelect: (k) => k != null && setActiveAccordionKey(k),
      }
    : {
        defaultActiveKey: defaultAccordionKey,
      };

  return (
    <div className={inModal ? 'ficha-modal-form' : undefined}>
      {inModal ? <FichaModalIntro /> : null}
      <div className={inModal ? 'ficha-modal-layout' : undefined}>
        {inModal ? (
          <FichaModalNav activeKey={activeAccordionKey} onSelect={setActiveAccordionKey} />
        ) : null}
        <div className={inModal ? 'ficha-modal-main' : undefined}>
          <Accordion
            key={`ficha-acc-${accordionMountKey}-${defaultAccordionKey}${inModal ? '-modal' : ''}`}
            flush
            className={`ficha-odonto-accordion${inModal ? ' ficha-odonto-accordion--modal' : ''}`}
            {...accordionProps}
          >
      <Accordion.Item eventKey="personal" className="ficha-acc-item ficha-acc-item--personal">
        <FichaAccHeader sectionKey={FICHA_ACCORDION_KEYS.personal} />
        <Accordion.Body className="ficha-acc-body">
          <FichaFormBlock title="Identificación del paciente" icon="bi-person-badge" hint="Nombre y documento de identidad">
          <Row className="g-3 ficha-form-row">
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Nombre completo <span className="text-danger">*</span></Form.Label>
                <Form.Control value={form.nombre || ''} onChange={(e) => update('nombre', e.target.value)} placeholder="Nombre y apellidos (se puede guardar después con solo teléfono o doc.)" />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Tipo de identificación <span className="text-danger">*</span></Form.Label>
                <Form.Select value={form.tipoIdentificacion || ''} onChange={(e) => update('tipoIdentificacion', e.target.value)}>
                  {OPTS_TIPO_IDENTIFICACION.map((o) => <option key={o.value || 'x'} value={o.value}>{o.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Número de identificación <span className="text-danger">*</span></Form.Label>
                <Form.Control value={form.cedula ?? ''} onChange={(e) => update('cedula', e.target.value)} placeholder={placeholderNumIdent} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Fecha nacimiento <span className="text-danger">*</span></Form.Label>
                <Form.Control type="date" value={form.fechaNacimiento || ''} onChange={(e) => update('fechaNacimiento', e.target.value)} title="Formato DD/MM/AAAA" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Género <span className="text-danger">*</span></Form.Label>
                <Form.Select value={form.sexo || ''} onChange={(e) => update('sexo', e.target.value)}>
                  {OPTS_SEXO.map((o) => <option key={o.value || 'x'} value={o.value}>{o.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Ocupación</Form.Label>
                <Form.Control value={form.ocupacion || ''} onChange={(e) => update('ocupacion', e.target.value)} placeholder="Profesión o actividad" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Estado civil</Form.Label>
                <Form.Select value={form.estadoCivil || ''} onChange={(e) => update('estadoCivil', e.target.value)}>
                  {OPTS_ESTADO_CIVIL.map((o) => <option key={o || 'x'} value={o}>{o || '—'}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          </FichaFormBlock>

          <FichaFormBlock title="Contacto" icon="bi-telephone">
          <Row className="g-3 ficha-form-row">
            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="small">Teléfono <span className="text-danger">*</span></Form.Label>
                <Form.Control value={form.telefono || ''} onChange={(e) => update('telefono', e.target.value)} placeholder="+593 98 123 4567" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-0">
                <Form.Label className="small">Email</Form.Label>
                <Form.Control type="email" value={form.email || ''} onChange={(e) => update('email', e.target.value)} placeholder="email@ejemplo.com" />
              </Form.Group>
            </Col>
          </Row>
          </FichaFormBlock>

          <FichaFormBlock title="Domicilio" icon="bi-geo-alt-fill" hint="Provincia, cantón y dirección de residencia">
          <Row className="g-3 ficha-form-row">
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Provincia <span className="text-danger">*</span></Form.Label>
                <Form.Select value={form.direccionProvincia || ''} onChange={onProvinciaChange}>
                  <option value="">— Seleccionar provincia —</option>
                  {opcionesConValorGuardado(provinciasEcuador, form.direccionProvincia).map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Cantón <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={form.direccionCanton || ''}
                  onChange={onCantonChange}
                  disabled={!form.direccionProvincia}
                >
                  <option value="">{form.direccionProvincia ? '— Seleccionar cantón —' : 'Primero elija provincia'}</option>
                  {opcionesConValorGuardado(cantonesEcuador, form.direccionCanton).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Ciudad / parroquia <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={form.direccionCiudad || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => ({
                      ...f,
                      direccionCiudad: v,
                      direccionParroquia: f.direccionParroquia || v,
                    }));
                  }}
                  disabled={!form.direccionCanton}
                >
                  <option value="">{form.direccionCanton ? '— Seleccionar ciudad —' : 'Primero elija cantón'}</option>
                  {opcionesConValorGuardado(localidadesCanton, form.direccionCiudad).map((ci) => (
                    <option key={ci} value={ci}>{ci}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Parroquia</Form.Label>
                <Form.Select
                  value={form.direccionParroquia || ''}
                  onChange={(e) => update('direccionParroquia', e.target.value)}
                  disabled={!form.direccionCanton}
                >
                  <option value="">{form.direccionCanton ? '— Opcional —' : 'Primero elija cantón'}</option>
                  {opcionesConValorGuardado(localidadesCanton, form.direccionParroquia).map((pa) => (
                    <option key={`pa-${pa}`} value={pa}>{pa}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Dirección exacta <span className="text-danger">*</span></Form.Label>
                <Form.Control value={form.direccionExacta || ''} onChange={(e) => update('direccionExacta', e.target.value)} placeholder="Calle, número, referencia, piso..." />
              </Form.Group>
            </Col>
          </Row>
          </FichaFormBlock>

          <FichaFormBlock title="Contactos de emergencia" icon="bi-exclamation-triangle-fill" hint="Persona a llamar si el paciente no puede comunicarse">
          <Row className="g-3 ficha-form-row">
            <Col xs={12}>
              <div className="ficha-emergencia-list">
                {filasContactoUi.map((row, i) => {
                  const tieneDatos = rowTieneContactoEmergencia(row);
                  const esFilaVacia = !tieneDatos;
                  return (
                    <div
                      key={`ce-edit-${i}`}
                      className={`ficha-emergencia-card${esFilaVacia ? ' ficha-emergencia-card--draft' : ''}`}
                    >
                      <div className="ficha-emergencia-card__head">
                        <span className="ficha-emergencia-card__badge">
                          <i className="bi bi-person-lines-fill" aria-hidden />
                          {tieneDatos ? `Contacto ${i + 1}` : 'Nuevo contacto'}
                        </span>
                        {(tieneDatos || filasContactoUi.length > 1) && (
                          <button
                            type="button"
                            className="ficha-emergencia-card__remove"
                            title={esFilaVacia ? 'Limpiar' : 'Eliminar contacto'}
                            onClick={() => removeContactoEmergencia(i)}
                          >
                            <i className="bi bi-trash" />
                            <span className="visually-hidden">Eliminar</span>
                          </button>
                        )}
                      </div>
                      <div className="ficha-emergencia-grid">
                        <Form.Group>
                          <Form.Label className="ficha-emergencia-label">Nombre completo</Form.Label>
                          <Form.Control
                            value={row.nombre}
                            onChange={(e) => updateContactoEmergencia(i, 'nombre', e.target.value)}
                            placeholder="Ej. María José Pérez"
                            autoComplete="name"
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label className="ficha-emergencia-label">Teléfono</Form.Label>
                          <div className="ficha-emergencia-input-icon">
                            <i className="bi bi-telephone" aria-hidden />
                            <Form.Control
                              type="tel"
                              value={row.telefono}
                              onChange={(e) => updateContactoEmergencia(i, 'telefono', e.target.value)}
                              placeholder="099 123 4567"
                              autoComplete="tel"
                            />
                          </div>
                        </Form.Group>
                        <Form.Group>
                          <Form.Label className="ficha-emergencia-label">Parentesco</Form.Label>
                          <Form.Select
                            value={row.relacion || ''}
                            onChange={(e) => updateContactoEmergencia(i, 'relacion', e.target.value)}
                          >
                            <option value="">Seleccione parentesco</option>
                            {PARENTESCO_EMERGENCIA.filter(Boolean).map((p) => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                            {row.relacion && !PARENTESCO_EMERGENCIA.includes(row.relacion) && (
                              <option value={row.relacion}>{row.relacion}</option>
                            )}
                          </Form.Select>
                        </Form.Group>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button type="button" className="cp-btn cp-btn--ghost cp-btn--sm mt-2" onClick={addContactoEmergencia}>
                <i className="bi bi-plus-lg" /> Añadir otro contacto
              </button>
            </Col>
          </Row>
          </FichaFormBlock>
        </Accordion.Body>
      </Accordion.Item>

      <Accordion.Item eventKey="medica" className="ficha-acc-item ficha-acc-item--medica">
        <FichaAccHeader sectionKey={FICHA_ACCORDION_KEYS.medica} />
        <Accordion.Body className="ficha-acc-body">
          <Row className="g-2">
            <Col xs={12}>
              <Form.Label className="small d-block">Alergias <span className="text-danger">*</span></Form.Label>
              <div className="d-flex flex-wrap gap-3 mb-2">
                {alergiasTiposLista.map(({ id, label }) => (
                  <Form.Check
                    key={id}
                    id={`alerg_${id}`}
                    type="checkbox"
                    checked={ensureArr(form.alergiasTipos).includes(id)}
                    onChange={(e) => toggleEnLista('alergiasTipos', id, e.target.checked)}
                    label={label}
                    className="small"
                  />
                ))}
              </div>
              <Form.Group className="mb-2">
                <Form.Label className="small text-muted">Detalle / especificación (qué medicamento, síntomas, observaciones)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.alergiasEspecificacion || ''}
                  onChange={(e) => update('alergiasEspecificacion', e.target.value)}
                  placeholder="Ej. Penicilina, mariscos, reacciones leves ante látex…"
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Label className="small d-block mb-2">Medicamentos actuales <span className="text-danger">*</span></Form.Label>
              <div className="ficha-emergencia-list">
                {filasMedUi.map((row, i) => {
                  const tieneDatos = rowTieneMedicamento(row);
                  const esFilaVacia = !tieneDatos;
                  return (
                    <div
                      key={`med-edit-${i}`}
                      className={`ficha-emergencia-card${esFilaVacia ? ' ficha-emergencia-card--draft' : ''}`}
                    >
                      <div className="ficha-emergencia-card__head">
                        <span className="ficha-emergencia-card__badge">
                          <i className="bi bi-capsule" aria-hidden />
                          {tieneDatos ? `Medicamento ${i + 1}` : 'Nuevo medicamento'}
                        </span>
                        {(tieneDatos || filasMedUi.length > 1) && (
                          <button
                            type="button"
                            className="ficha-emergencia-card__remove"
                            title={esFilaVacia ? 'Limpiar' : 'Eliminar medicamento'}
                            onClick={() => removeMedFila(i)}
                          >
                            <i className="bi bi-trash" />
                            <span className="visually-hidden">Eliminar</span>
                          </button>
                        )}
                      </div>
                      <div className="ficha-emergencia-grid ficha-emergencia-grid--meds">
                        <Form.Group>
                          <Form.Label className="ficha-emergencia-label">Nombre</Form.Label>
                          <Form.Control
                            value={row.nombre}
                            onChange={(e) => updateMedFila(i, 'nombre', e.target.value)}
                            placeholder="Ej. Ibuprofeno"
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label className="ficha-emergencia-label">Dosis</Form.Label>
                          <Form.Control
                            value={row.dosis}
                            onChange={(e) => updateMedFila(i, 'dosis', e.target.value)}
                            placeholder="Ej. 400 mg"
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label className="ficha-emergencia-label">Frecuencia</Form.Label>
                          <Form.Control
                            value={row.frecuencia}
                            onChange={(e) => updateMedFila(i, 'frecuencia', e.target.value)}
                            placeholder="Ej. cada 8 h"
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label className="ficha-emergencia-label">Vía de administración</Form.Label>
                          <Form.Select value={row.via} onChange={(e) => updateMedFila(i, 'via', e.target.value)}>
                            {viasAdministracionLista.map((v, vi) => (
                              <option key={`${String(v)}_${vi}`} value={v}>{v || 'Seleccione vía'}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button type="button" className="cp-btn cp-btn--ghost cp-btn--sm mt-2" onClick={addMedFila}>
                <i className="bi bi-plus-lg" /> Añadir otro medicamento
              </button>
            </Col>
            <Col xs={12}>
              <Form.Label className="small">Enfermedades crónicas <span className="text-danger">*</span></Form.Label>
              <div className="row row-cols-1 row-cols-md-2 g-2">
                {enfCronicasLista.map(({ id, label }) => (
                  <Col key={id}>
                    <Form.Check
                      id={`enc_${id}`}
                      type="checkbox"
                      checked={ensureArr(form.enfermedadesCronicasSeleccion).includes(id)}
                      onChange={(e) => toggleEnLista('enfermedadesCronicasSeleccion', id, e.target.checked)}
                      label={label}
                      className="small"
                    />
                  </Col>
                ))}
              </div>
              <div className="mt-2">
                <Form.Check
                  id="enc_otros_ck"
                  type="checkbox"
                  checked={ensureArr(form.enfermedadesCronicasSeleccion).includes('enf_otros')}
                  onChange={(e) => toggleEnLista('enfermedadesCronicasSeleccion', 'enf_otros', e.target.checked)}
                  label={<span>Otros (especificar)</span>}
                  className="small mb-1"
                />
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.enfermedadesCronicasOtro || ''}
                  onChange={(e) => update('enfermedadesCronicasOtro', e.target.value)}
                  placeholder="Otras enfermedades crónicas, detalles o antecedentes no listados arriba"
                />
              </div>
            </Col>
            <Col xs={12}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Cirugías previas</Form.Label>
                <Form.Control as="textarea" rows={1} value={form.cirugiasPrevias || ''} onChange={(e) => update('cirugiasPrevias', e.target.value)} placeholder="Incluir orales o maxilofaciales, fechas" />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Label className="small">Hábitos (marcar todas las que apliquen)</Form.Label>
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-2">
                {habitosLista.map(({ id, label }) => (
                  <Col key={id}>
                    <Form.Check
                      id={`hab_${id}`}
                      type="checkbox"
                      checked={ensureArr(form.habitosSeleccion).includes(id)}
                      onChange={(e) => toggleEnLista('habitosSeleccion', id, e.target.checked)}
                      label={label}
                      className="small"
                    />
                  </Col>
                ))}
              </div>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Embarazo (si aplica)</Form.Label>
                <Form.Select value={form.embarazo || ''} onChange={(e) => update('embarazo', e.target.value)}>
                  {OPTS_SI_NO.map((o) => <option key={o || 'x'} value={o}>{o || '—'}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Trimestre</Form.Label>
                <Form.Control value={form.embarazoTrimestre || ''} onChange={(e) => update('embarazoTrimestre', e.target.value)} placeholder="1, 2 o 3" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Vacunaciones</Form.Label>
                <Form.Control value={form.vacunaciones || ''} onChange={(e) => update('vacunaciones', e.target.value)} placeholder="Ej. Hepatitis B" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Grupo sanguíneo y factor Rh</Form.Label>
                <Form.Select value={form.grupoSanguineo || ''} onChange={(e) => update('grupoSanguineo', e.target.value)}>
                  {OPTS_GRUPO_SANGUINEO.map((o) => <option key={o || 'x'} value={o}>{o || '—'}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Historial médico (notas adicionales)</Form.Label>
                <Form.Control as="textarea" rows={1} value={form.historialMedico || ''} onChange={(e) => update('historialMedico', e.target.value)} />
              </Form.Group>
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>

      <Accordion.Item eventKey="odontologica" className="ficha-acc-item ficha-acc-item--odontologica">
        <FichaAccHeader sectionKey={FICHA_ACCORDION_KEYS.odontologica} />
        <Accordion.Body className="ficha-acc-body">
          <Row className="g-2">
            <Col xs={12}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Motivo de consulta <span className="text-danger">*</span></Form.Label>
                <Form.Control as="textarea" rows={1} value={form.motivoConsulta || ''} onChange={(e) => update('motivoConsulta', e.target.value)} placeholder="Dolor, revisión rutinaria, etc." />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Enfermedad o problema actual (describir) <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.enfermedadActual || ''}
                  onChange={(e) => update('enfermedadActual', e.target.value)}
                  placeholder="Descripción clínica del problema actual que motivó la valoración dental…"
                />
              </Form.Group>
            </Col>

            <Col xs={12} className="mt-2">
              <div className="small fw-semibold text-muted mb-2">Signos vitales</div>
              <div className="table-responsive border rounded">
                <table className="table table-sm align-middle mb-0 small">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '38%' }}>Parámetro</th>
                      <th>Valor / medición</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SIGNOS_ODONTO_BASE_ROWS.map(({ key, label }) => (
                      <tr key={key}>
                        <td className="text-muted">{label}</td>
                        <td className="p-1">
                          <Form.Control size="sm" value={form[key] ?? ''} onChange={(e) => update(key, e.target.value)} placeholder="Ej. 120/80, 72 lpm…" />
                        </td>
                      </tr>
                    ))}
                    {odontoSignosExtras.map((ex) => (
                      <tr key={ex.id}>
                        <td className="text-muted">{ex.label}</td>
                        <td className="p-1">
                          <Form.Control
                            size="sm"
                            value={ensureObj(form.signosVitalesExtrasValores)[ex.id] ?? ''}
                            onChange={(e) => patchObjCampoPaciente('signosVitalesExtrasValores', ex.id, e.target.value)}
                            placeholder="Valor"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Col>

            <Col xs={12} className="mt-3">
              <div className="small fw-semibold text-muted mb-2">Antecedentes personales y familiares</div>
              <p className="small text-muted mb-2 mb-md-1">
                Si marca <strong>Sí</strong>, indique el detalle en la columna derecha (ej. antibiótico al que es alérgico).
              </p>
              <div className="table-responsive border rounded">
                <table className="table table-sm align-middle mb-0 small ficha-antecedentes-table">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '32%' }}>Ítem</th>
                      <th style={{ width: '18%' }}>Refiere</th>
                      <th>Especifique (si Sí)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ANTECEDENTES_ODONTO_TABLA_BASE.map(({ key, label, detailPh }) => {
                      const refiere = form[key] || '';
                      const detalle = ensureObj(form.antecedentesOdontoDetalles)[key] ?? '';
                      const muestraDetalle = refiere === 'Sí';
                      return (
                        <tr key={key} className={muestraDetalle ? 'ficha-antecedentes-row--si' : ''}>
                          <td>{label}</td>
                          <td className="p-1">
                            <Form.Select
                              size="sm"
                              value={refiere}
                              onChange={(e) => handleAntecedenteRefiere(key, e.target.value)}
                              disabled={readOnly}
                            >
                              {OPTS_SI_NO.map((o) => (
                                <option key={o || 'sn_'} value={o}>{o || '—'}</option>
                              ))}
                            </Form.Select>
                          </td>
                          <td className="p-1">
                            {muestraDetalle ? (
                              readOnly ? (
                                <span>{detalle.trim() || '—'}</span>
                              ) : (
                                <Form.Control
                                  size="sm"
                                  className="ficha-antecedente-detalle-input"
                                  value={detalle}
                                  onChange={(e) => patchAntecedenteDetalle(key, e.target.value)}
                                  placeholder={detailPh || ANTECEDENTE_DETALLE_PH_DEFAULT}
                                  required
                                  aria-required="true"
                                  aria-label={`Detalle: ${label}`}
                                />
                              )
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {odontoAntecedenteExtras.map((ex) => {
                      const refiere = ensureObj(form.antecedentesOdontoExtrasValores)[ex.id] ?? '';
                      const detalle = ensureObj(form.antecedentesOdontoDetalles)[ex.id] ?? '';
                      const muestraDetalle = refiere === 'Sí';
                      return (
                        <tr key={ex.id} className={muestraDetalle ? 'ficha-antecedentes-row--si' : ''}>
                          <td>{ex.label}</td>
                          <td className="p-1">
                            <Form.Select
                              size="sm"
                              value={refiere}
                              onChange={(e) => handleAntecedenteExtraRefiere(ex.id, e.target.value)}
                              disabled={readOnly}
                            >
                              {OPTS_SI_NO.map((o) => (
                                <option key={`${ex.id}_${o || '_'}`} value={o}>{o || '—'}</option>
                              ))}
                            </Form.Select>
                          </td>
                          <td className="p-1">
                            {muestraDetalle ? (
                              readOnly ? (
                                <span>{detalle.trim() || '—'}</span>
                              ) : (
                                <Form.Control
                                  size="sm"
                                  className="ficha-antecedente-detalle-input"
                                  value={detalle}
                                  onChange={(e) => patchAntecedenteDetalle(ex.id, e.target.value)}
                                  placeholder={ANTECEDENTE_DETALLE_PH_DEFAULT}
                                  required
                                  aria-required="true"
                                  aria-label={`Detalle: ${ex.label}`}
                                />
                              )
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td colSpan={2} className="bg-light py-2 small fw-semibold">Otro (especificaciones)</td>
                    </tr>
                    <tr>
                      <td colSpan={2} className="p-2 pt-0">
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={form.otrosAntecedentes || ''}
                          onChange={(e) => update('otrosAntecedentes', e.target.value)}
                          placeholder="Antecedentes o detalles adicionales no listados…"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Col>

            <Col xs={12} className="mt-3">
              <ExamenEstomatognaticoGrid
                form={form}
                setForm={setForm}
                readOnly={readOnly}
                extras={odontoExamenExtras}
              />
            </Col>

            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Fecha última visita odontológica</Form.Label>
                <Form.Control type="date" value={form.fechaUltimaVisitaOdontologica ? form.fechaUltimaVisitaOdontologica.slice(0, 10) : ''} onChange={(e) => update('fechaUltimaVisitaOdontologica', e.target.value)} />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Tratamientos previos <span className="text-danger">*</span></Form.Label>
                <Form.Control as="textarea" rows={2} value={form.tratamientosPrevios || ''} onChange={(e) => update('tratamientosPrevios', e.target.value)} placeholder="Extracciones, empastes, ortodoncia, implantes, fechas" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Higiene bucal <span className="text-danger">*</span></Form.Label>
                <Form.Select value={form.higieneBucal || ''} onChange={(e) => update('higieneBucal', e.target.value)}>
                  {odontoHigieneOpciones.map((o, oi) => (
                    <option key={`${o || 'hg'}_${oi}`} value={o}>{o || '—'}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Dolor o síntomas actuales <span className="text-danger">*</span></Form.Label>
                <Form.Control as="textarea" rows={2} value={form.dolorSintomasActuales || ''} onChange={(e) => update('dolorSintomasActuales', e.target.value)} placeholder="Descripción, localización (use el odontograma para marcar)" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Intensidad del dolor (1-10)</Form.Label>
                <Form.Control type="number" min={0} max={10} value={form.intensidadDolor ?? ''} onChange={(e) => update('intensidadDolor', e.target.value ? Number(e.target.value) : '')} placeholder="0-10" />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Enfermedades bucales crónicas</Form.Label>
                <Form.Control as="textarea" rows={1} value={form.enfermedadesBucalesCronicas || ''} onChange={(e) => update('enfermedadesBucalesCronicas', e.target.value)} placeholder="Bruxismo, periodontitis, halitosis" />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Prótesis o aparatos</Form.Label>
                <Form.Control value={form.protesisAparatos || ''} onChange={(e) => update('protesisAparatos', e.target.value)} placeholder="Dentaduras, brackets, retenedores, fecha colocación" />
              </Form.Group>
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>

      <Accordion.Item eventKey="consentimiento" className="ficha-acc-item ficha-acc-item--consentimiento">
        <FichaAccHeader sectionKey={FICHA_ACCORDION_KEYS.consentimiento} />
        <Accordion.Body className="ficha-acc-body">
          <ConsentimientoInformadoSection
            form={form}
            setForm={setForm}
            tenantId={tenantId}
            idDoctor={idDoctor}
          />
        </Accordion.Body>
      </Accordion.Item>

      <Accordion.Item eventKey="msp-odonto" className="ficha-acc-item ficha-acc-item--msp">
        <FichaAccHeader sectionKey={FICHA_ACCORDION_KEYS.msp} />
        <Accordion.Body className="ficha-acc-body">
          <div className="ficha-msp-banner">
            <i className="bi bi-clipboard2-data" aria-hidden />
            <p>
              Complementa el odontograma del paciente. Los índices y planes siguen nomenclatura de historia clínica MSP.
            </p>
          </div>
          <FichaFormBlock title="Indicadores de salud bucal" icon="bi-droplet-half">
          <IndicadoresOdontogramaPanel form={form} setForm={setForm} />
          </FichaFormBlock>

          <FichaFormBlock title="Exámenes complementarios" icon="bi-microscope">
          <Row className="g-3 ficha-form-row">
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Biometría</Form.Label>
                <Form.Control value={form.biometria || ''} onChange={(e) => update('biometria', e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Química sanguínea</Form.Label>
                <Form.Control value={form.quimicaSanguinea || ''} onChange={(e) => update('quimicaSanguinea', e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Rayos X</Form.Label>
                <Form.Control value={form.rayosX || ''} onChange={(e) => update('rayosX', e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Otros exámenes</Form.Label>
                <Form.Control value={form.otrosExamenes || ''} onChange={(e) => update('otrosExamenes', e.target.value)} />
              </Form.Group>
            </Col>
          </Row>
          <Row className="g-3 ficha-form-row mt-1">
            <Col xs={12}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Plan diagnóstico · terapéutico · educativo</Form.Label>
                <Form.Control as="textarea" rows={3} value={form.planDiagTerapeuticoEducativoMSP || ''} onChange={(e) => update('planDiagTerapeuticoEducativoMSP', e.target.value)} placeholder="Planes clínicos, educación higiénica…" />
              </Form.Group>
            </Col>
          </Row>
          </FichaFormBlock>

          <FichaFormBlock title="Plan de diagnóstico MSP" icon="bi-search">
            <PlanDiagnosticoMspTable
              rows={form.tablaDiagnosticosMSP}
              onChange={(next) => setForm((f) => ({ ...f, tablaDiagnosticosMSP: next }))}
              showSectionTitle={false}
            />
          </FichaFormBlock>

          <FichaFormBlock title="Plan de tratamiento MSP" icon="bi-clipboard2-pulse">
            <PlanTratamientoMspTable
              rows={form.tablaTratamientosMSP}
              onChange={(next) => setForm((f) => ({ ...f, tablaTratamientosMSP: next }))}
              showSectionTitle={false}
            />
          </FichaFormBlock>

          <FichaFormBlock title="Resumen de tratamiento" icon="bi-journal-medical">
          <Row className="g-3 ficha-form-row">
            <Col xs={12}>
              <Form.Group className="mb-0">
                <Form.Label className="small">Tratamiento — resumen global (opcional)</Form.Label>
                <Form.Control as="textarea" rows={3} value={form.tratamientoMSPTextoResumen || ''} onChange={(e) => update('tratamientoMSPTextoResumen', e.target.value)} placeholder="Resumen u observaciones que complementen la tabla." />
              </Form.Group>
            </Col>
          </Row>
          </FichaFormBlock>
        </Accordion.Body>
      </Accordion.Item>

      <Accordion.Item eventKey="seguro" className="ficha-acc-item ficha-acc-item--seguro">
        <FichaAccHeader sectionKey={FICHA_ACCORDION_KEYS.seguro} />
        <Accordion.Body className="ficha-acc-body">
          <Row className="g-3 ficha-form-row">
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Tipo de seguro</Form.Label>
                <Form.Select value={form.tipoSeguro || ''} onChange={(e) => update('tipoSeguro', e.target.value)}>
                  {OPTS_TIPO_SEGURO.map((o) => <option key={o || 'x'} value={o}>{o || '—'}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Nombre del seguro</Form.Label>
                <Form.Control value={form.seguro || ''} onChange={(e) => update('seguro', e.target.value)} placeholder="IESS, compañía privada" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Número de póliza</Form.Label>
                <Form.Control value={form.numeroSeguro || ''} onChange={(e) => update('numeroSeguro', e.target.value)} placeholder="Nº póliza" />
              </Form.Group>
            </Col>
            {form.fechaRegistro && (
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small text-muted">Fecha de registro</Form.Label>
                  <Form.Control plaintext readOnly className="small text-muted" value={new Date(form.fechaRegistro).toLocaleString('es-EC')} />
                </Form.Group>
              </Col>
            )}
            <Col xs={12}>
              <Form.Group className="mb-2">
                <Form.Label className="small">Notas adicionales</Form.Label>
                <Form.Control as="textarea" rows={2} value={form.notas || ''} onChange={(e) => update('notas', e.target.value)} placeholder="Observaciones del personal médico" />
              </Form.Group>
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
        </div>
      </div>
    </div>
  );
};

function MedicamentosActualesLectura({ form }) {
  const filas = sanitizeMedicamentosFilas(form.medicamentosActualesFilas).filter(rowTieneMedicamento);
  const legacy = (form.medicamentosActuales || '').trim();
  if (filas.length > 0) {
    return (
      <Col xs={12}>
        <div className="ficha-odonto-field-view">
          <span className="ficha-odonto-field-label">Medicamentos actuales</span>
          <div className="table-responsive mt-1">
            <table className="table table-sm table-bordered mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Nombre</th>
                  <th>Dosis</th>
                  <th>Frecuencia</th>
                  <th>Vía adm.</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((r, idx) => (
                  <tr key={idx}>
                    <td>{r.nombre || '—'}</td>
                    <td>{r.dosis || '—'}</td>
                    <td>{r.frecuencia || '—'}</td>
                    <td>{r.via || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Col>
    );
  }
  if (legacy) {
    return <Col xs={12}><FieldView label="Medicamentos actuales" value={legacy} /></Col>;
  }
  return null;
}


function FieldView({ label, value, highlight }) {
  if (value === undefined || value === null || value === '') return null;
  const v = typeof value === 'boolean' ? (value ? 'Sí' : 'No') : String(value);
  return (
    <div className={`ficha-odonto-field-view ${highlight ? 'ficha-odonto-field-view--highlight' : ''}`}>
      <span className="ficha-odonto-field-label">{label}</span>
      <span className="ficha-odonto-field-value">{v}</span>
    </div>
  );
}

export default FichaOdontologicaForm;
